import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { getCities } from '@/lib/api';

export default function HomePage() {
  const [cities, setCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await getCities(true);
        setCities(response.data);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCityClick = (city) => {
    navigate(`/${city.slug}`);
  };

  return (
    <div className="min-h-screen bg-background page-enter">
      {/* Hero Section */}
      <div className="relative min-h-[60vh] flex flex-col items-center justify-center px-6 py-16">
        {/* Background glow */}
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 mb-12"
        >
          <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl font-semibold tracking-tight">
            <span className="text-foreground">Firma</span>
            <span className="text-primary">Burada</span>
          </h1>
          <p className="text-muted-foreground text-center mt-4 text-lg">
            Türkiye'nin En Kapsamlı Firma Rehberi
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-xl"
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="city-search-input"
              type="text"
              placeholder="Şehir ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-6 text-lg bg-card border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
        </motion.div>
      </div>

      {/* Cities Grid */}
      <div className="px-6 md:px-12 lg:px-24 pb-24">
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-heading text-2xl font-semibold mb-8 text-foreground"
        >
          Şehirler
        </motion.h2>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="h-40 bg-card rounded-xl animate-pulse"
              />
            ))}
          </div>
        ) : filteredCities.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">
              {searchQuery ? 'Şehir bulunamadı' : 'Henüz şehir eklenmemiş'}
            </p>
            <p className="text-muted-foreground/60 mt-2">
              Admin panelinden şehir ekleyebilirsiniz
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredCities.map((city, index) => (
              <motion.div
                key={city.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  delay: index * 0.05, 
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1]
                }}
              >
                <button
                  data-testid={`city-card-${city.slug}`}
                  onClick={() => handleCityClick(city)}
                  className="relative w-full h-40 rounded-xl overflow-hidden group cursor-pointer"
                >
                  {/* Background */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{
                      backgroundImage: city.image_url 
                        ? `url(${city.image_url})`
                        : 'linear-gradient(135deg, hsl(var(--secondary)) 0%, hsl(var(--card)) 100%)'
                    }}
                  />
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  {/* Border glow on hover */}
                  <div className="absolute inset-0 border border-white/0 group-hover:border-primary/50 rounded-xl transition-colors duration-300" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 p-4 transform transition-transform duration-300 group-hover:-translate-y-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="font-heading font-semibold text-lg text-white">
                        {city.name}
                      </span>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground text-sm">
            © 2025 FirmaBurada. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
}
